import React from 'react'
import Prism from 'prismjs'
import { RichText } from 'prismic-reactjs'
import Zoom from 'react-medium-image-zoom'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import 'react-medium-image-zoom/dist/styles.css'
import { iconGithub, iconLinkedin, iconTwitter, iconDiscord, iconReddit, iconHackernews, iconShare } from 'utils/icons'
import { COMMON } from 'utils/intl/common.js'
import { STATUS_SUCCESS } from 'utils/constants.js'

export const getIconService = (icon) => {
  return <img className={`icon-service icon-${icon}`} src={`/images/icons/${icon}.svg`} alt={icon} />
}

export const getIconSocial = (icon) => {
  switch (icon) {
    case 'github':
      return iconGithub
    case 'linkedin':
      return iconLinkedin
    case 'twitter':
      return iconTwitter
    default:
      return iconDiscord
  }
}

export const getIconShare = (icon) => {
  switch (icon) {
    case 'reddit':
      return iconReddit
    case 'linkedin':
      return iconLinkedin
    case 'twitter':
      return iconTwitter
    case 'hackernews':
      return iconHackernews
    default:
      return iconShare
  }
}

export function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export function convertDate(date, small) {
  const d = new Date(date)
  const options = { year: 'numeric', month: !small ? 'long' : 'short', day: 'numeric' }
  return d.toLocaleDateString('en-US', options)
}

export function readingTime(content) {
  const wpm = 225
  const words = content.trim().split(/\s+/).length
  return Math.ceil(words / wpm)
}

export function cropString(str, num) {
  if (str.length > num) {
    return str.slice(0, num) + '...'
  } else {
    return str
  }
}

export function copyLink() {
  const el = document.createElement('input')
  el.value = window.location.href
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)
}

export function copyText(text, dispatch) {
  const inputc = document.body.appendChild(document.createElement('input'))
  inputc.style.position = 'fixed'
  inputc.style.bottom = 0
  inputc.style.left = 0
  inputc.value = text
  inputc.focus()
  inputc.select()
  document.execCommand('copy')
  inputc.parentNode.removeChild(inputc)

  dispatch &&
    dispatch({
      message: {
        isOpen: true,
        text: COMMON.COPIED_CLIPBOARD,
        status: STATUS_SUCCESS,
      },
    })
}

// Constant to check if is HTML element or code line
const IS_DOMELEMENT = 'Q-DOMELEMENT'
// Constant to check if is Markdown
const IS_MARKDOWN = 'Q-MARKDOWN'

export const createId = (string = '') =>
  string
    ?.toLowerCase()
    .replace(/[^\w\s]/gi, '')
    .replace(/\s+/g, '-')

export function impContentSummary(content) {
  return (
    <ul>
      {content.map((element, index) => {
        if (element.type === 'heading2') {
          return (
            <li key={index} className="ft-tertiary mb-1">
              <a href={`#${createId(element.text)}`} className="ft-500 c-text-black-lighter">
                {element.text}
              </a>
            </li>
          )
        }

        if (element.type === 'heading3') {
          return (
            <li key={index} className="ml-2 ft-tertiary mb-1">
              <a href={`#${createId(element.text)}`} className="c-text-black-lighter">
                - {element.text}
              </a>
            </li>
          )
        }
        return null
      })}
    </ul>
  )
}

export function impContent(content, dispatch) {
  const htmlSerializer = function (type, element, content, children, key) {
    if (type === 'preformatted') {
      const text = element.text

      if (text.includes(IS_DOMELEMENT)) {
        // Format with custom HTML
        return <div dangerouslySetInnerHTML={{ __html: text.replace(IS_DOMELEMENT, '') }} />
      } else if (text.includes(IS_MARKDOWN)) {
        // Format with Markdown
        return (
          <Markdown key={key} remarkPlugins={[remarkGfm]}>
            {text.replace(IS_MARKDOWN, '')}
          </Markdown>
        )
      } else {
        // Format code with Prismjs
        const codeFormat = Prism.highlight(text, Prism.languages.javascript, 'javascript{numberLines: true}')
        return (
          <div key={key} className="block-code">
            <div
              role="button"
              tabIndex="0"
              className="block-code__copy"
              onClick={() => copyText(text, dispatch)}
              onKeyDown={() => copyText(text, dispatch)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 15 15">
                <path
                  fill="#A097F2"
                  d="M14.727 2.591L13.059.923a.89.89 0 00-.63-.273H7.974c-.985 0-1.75.793-1.75 1.75v7c.027.984.793 1.75 1.777 1.75h5.25c.957 0 1.75-.766 1.75-1.75V3.22a.89.89 0 00-.273-.629zM13.687 9.4a.45.45 0 01-.437.437H7.973a.432.432 0 01-.438-.437V2.427c0-.246.192-.438.438-.438h3.5l.027 1.286c0 .492.383.875.875.875h1.285V9.4h.027zm-5.25 3.5a.45.45 0 01-.437.437H2.723a.432.432 0 01-.438-.437V5.927a.45.45 0 01.438-.438h2.652V4.15H2.723c-.957 0-1.75.794-1.75 1.75l.027 7c0 .985.766 1.75 1.75 1.75H8c.957 0 1.75-.765 1.75-1.75v-.874H8.437v.875z"
                ></path>
              </svg>
            </div>
            <pre className="language-js">
              <code dangerouslySetInnerHTML={{ __html: codeFormat }}></code>
            </pre>
          </div>
        )
      }
    }

    if (type === 'image') {
      return (
        <figure className="mb-5 mt-5" key={key}>
          <div className="f f-jc-center">
            <Zoom>
              <img loading="lazy" src={element.url} alt={element.alt} />
            </Zoom>
          </div>
          {element.alt !== '' && (
            <figcaption className="ft-center ft-tertiary c-text-black-lightest">{element.alt}</figcaption>
          )}
        </figure>
      )
    }

    if (type === 'heading2') {
      return (
        <h2 id={createId(element.text)} className="article__content__title">
          <a href={`#${createId(element.text)}`}>
            <span role="button" tabIndex="0" onClick={() => copyLink()} onKeyDown={() => copyLink()}>
              #
            </span>
            {element.text}
          </a>
        </h2>
      )
    }

    if (type === 'heading3') {
      return (
        <h3 id={createId(element.text)} className="article__content__title">
          <a href={`#${createId(element.text)}`}>
            <span role="button" tabIndex="0" onClick={() => copyLink()} onKeyDown={() => copyLink()}>
              #
            </span>
            {element.text}
          </a>
        </h3>
      )
    }
  }

  return <RichText render={content} htmlSerializer={htmlSerializer} />
}

export function checkScrollDirectionIsUp(event) {
  if (event.wheelDelta) {
    return event.wheelDelta > 0
  }
  return event.deltaY < 0
}
